import { LocationStrategy } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  enableProdMode,
  EnvironmentProviders,
  importProvidersFrom,
  makeEnvironmentProviders,
  Provider,
  Type,
} from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { createApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { DsFormsModule, DsImprintModule } from '@bmw-ds/components';
import { OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';
import { LoggerModule, NgxLoggerLevel, TOKEN_LOGGER_SERVER_SERVICE } from 'ngx-logger';

import { authHeaderInterceptor } from './auth/authentication/auth-interceptor';
import { authRealmHeaderInterceptor } from './auth/authentication/auth-realm-interceptor';
import { AuthenticatedLoggerServerService } from './auth/authentication/authenticated-logger-server.service';
import { BASE_PATH, urlInterceptor } from './auth/authentication/url-interceptor';
import { DealerProvider } from './auth/dealer/dealer.provider';
import { environment } from './environments/environment';
import { routes } from './routes';
import { CustomerDataApp } from './src/customer-data.app';
import { CustomerDataWebComponent } from './src/customer-data.web-component';
import { CustomerSearchWebComponent } from './src/customer-search-page/components/customer-search.web-component';
import { HelloPage } from './src/hello/hello.page';
import { StackLocationStrategy } from './src/navigation/location.strategy';
import { ProfileWebComponent } from './src/profile-page/components/profile.web-component';
import { ReportingWebComponent } from './src/reporting-page/reporting.web-component';
import { AddCustomerToVehicleWebComponent } from './src/vehicle/components/add-customer-to-vehicle/add-customer-to-vehicle.web-component';

if (environment.production) {
  enableProdMode();
}

const globalProviders = makeEnvironmentProviders([
  provideAnimationsAsync(),
  provideHttpClient(withFetch(), withInterceptors([urlInterceptor, authHeaderInterceptor, authRealmHeaderInterceptor])),
  importProvidersFrom(
    LoggerModule.forRoot(
      {
        level: NgxLoggerLevel.DEBUG,

        // `serverLoggingUrl` must be set so that logs are sent to the server,
        // but the actual URL is determined dynamically in AuthenticatedLoggerServerService,
        // taking into account the base URL which can be set by input and can change at runtime.
        serverLoggingUrl: AuthenticatedLoggerServerService.loggingPath,
        serverLogLevel: NgxLoggerLevel.INFO,
        serverCallsBatchSize: 15,
        serverCallsTimer: 15000,
        withCredentials: true,
      },
      {
        serverProvider: {
          provide: TOKEN_LOGGER_SERVER_SERVICE,
          useClass: AuthenticatedLoggerServerService,
        },
      },
    ),
  ),
  importProvidersFrom(
    DsImprintModule.forRoot({
      electronicContact: ' ',
      phone: ' ',
      itsmService: 'Retail CRM',
      serviceOffering: 'rsp-crm#global',
    }),
  ),
  importProvidersFrom(DsFormsModule.forRoot({ markRequiredFormLabels: true })),
  DealerProvider,
  [{ provide: Location, useValue: window.location }],
  [{ provide: History, useValue: window.history }],
  [{ provide: BASE_PATH, useValue: '/api/customer-data-frontend' }],
  provideOAuthClient({
    resourceServer: {
      sendAccessToken: false,
    },
  }),
  { provide: OAuthStorage, useFactory: (): OAuthStorage => localStorage },
]);

export type ComponentName =
  | 'customer-data-frontend'
  | 'customer-data-hello'
  | 'customer-data-reporting'
  | 'customer-data-search'
  | 'customer-data-profile'
  | 'customer-data-app'
  | 'customer-data-add-customer-to-vehicle';

const providersAndElements: {
  providers?: (Provider | EnvironmentProviders)[];
  elements: Partial<Record<ComponentName, Type<object>>>;
}[] = [
  // Elements without Angular Router
  {
    elements: {
      'customer-data-frontend': CustomerDataWebComponent,
      'customer-data-hello': HelloPage,
      'customer-data-reporting': ReportingWebComponent,
      'customer-data-search': CustomerSearchWebComponent,
      'customer-data-profile': ProfileWebComponent,
      'customer-data-add-customer-to-vehicle': AddCustomerToVehicleWebComponent,
    },
  },

  // Elements with Angular Router
  {
    providers: [
      provideRouter(routes, withComponentInputBinding()),
      StackLocationStrategy,
      { provide: LocationStrategy, useExisting: StackLocationStrategy },
    ],
    elements: { 'customer-data-app': CustomerDataApp },
  },
];

for (const { providers = [], elements } of providersAndElements) {
  createApplication({ providers: [globalProviders, ...providers] })
    .then((app) => {
      for (const [elementName, component] of Object.entries(elements)) {
        const injector = app.injector;
        customElements.define(elementName, createCustomElement(component, { injector }));
      }
    })
    .catch((e) => {
      console.error('Cannot initialise app, unable to provide', Object.keys(elements), e);
    });
}
